import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Search, Filter, Target, BookOpen, Shuffle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Question {
  id: number;
  teacher_id: number;
  question_text: string;
  answer_text: string;
  question_type: 'multiple_choice' | 'true_false' | 'short_answer' | 'essay';
  subject: string;
  difficulty: 'easy' | 'medium' | 'hard';
  tags: string[];
  source_type: string;
  source_data: any;
  created_at: string;
  updated_at: string;
}

interface QuestionSelectorProps {
  onQuestionsSelected: (questions: Question[]) => void;
  maxQuestions?: number;
  prefilterCategory?: string;
  prefilterDifficulty?: string;
}

const QuestionSelector: React.FC<QuestionSelectorProps> = ({
  onQuestionsSelected,
  maxQuestions = 50,
  prefilterCategory = '',
  prefilterDifficulty = ''
}) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState<Question[]>([]);
  const [selectedQuestions, setSelectedQuestions] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState(prefilterCategory || 'all');
  const [filterDifficulty, setFilterDifficulty] = useState(prefilterDifficulty || 'all');
  const [filterType, setFilterType] = useState('all');
  const [filterTag, setFilterTag] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  const { toast } = useToast();
  const QUESTIONS_POOL_TABLE_ID = '16057';

  useEffect(() => {
    fetchQuestions();
  }, [currentPage, filterCategory, filterDifficulty, filterType]);

  useEffect(() => {
    filterQuestions();
  }, [questions, searchTerm, filterTag]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);

      const { data: userInfo, error: userError } = await window.ezsite.apis.getUserInfo();
      if (userError) throw userError;

      const filters = [
      { name: 'teacher_id', op: 'Equal', value: userInfo.ID }];


      if (filterCategory && filterCategory !== 'all') {
        filters.push({ name: 'subject', op: 'StringContains', value: filterCategory });
      }
      if (filterDifficulty && filterDifficulty !== 'all') {
        filters.push({ name: 'difficulty', op: 'Equal', value: filterDifficulty });
      }
      if (filterType && filterType !== 'all') {
        filters.push({ name: 'question_type', op: 'Equal', value: filterType });
      }

      const { data, error } = await window.ezsite.apis.tablePage(QUESTIONS_POOL_TABLE_ID, {
        PageNo: currentPage,
        PageSize: pageSize,
        OrderByField: 'created_at',
        IsAsc: false,
        Filters: filters
      });

      if (error) throw error;

      const questionsData = data.List.map((q: any) => ({
        ...q,
        tags: q.tags ? JSON.parse(q.tags) : [],
        source_data: q.source_data ? JSON.parse(q.source_data) : {}
      }));

      setQuestions(questionsData);
      setTotalPages(Math.ceil(data.VirtualCount / pageSize));

      // Extract unique categories and tags
      const uniqueCategories = [...new Set(questionsData.map((q: Question) => q.subject))];
      const uniqueTags = [...new Set(questionsData.flatMap((q: Question) => q.tags))];
      setCategories(uniqueCategories);
      setAllTags(uniqueTags);

    } catch (error) {
      console.error('Error fetching questions:', error);
      toast({
        title: "Error",
        description: "Failed to fetch questions",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filterQuestions = () => {
    let filtered = questions;

    if (searchTerm) {
      filtered = filtered.filter((question) =>
      question.question_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
      question.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      question.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (filterTag) {
      filtered = filtered.filter((question) =>
      question.tags.some((tag) => tag.toLowerCase().includes(filterTag.toLowerCase()))
      );
    }

    setFilteredQuestions(filtered);
  };

  const handleSelectQuestion = (questionId: number, checked: boolean) => {
    const newSelected = new Set(selectedQuestions);
    if (checked) {
      if (newSelected.size >= maxQuestions) {
        toast({
          title: "Selection Limit",
          description: `You can select up to ${maxQuestions} questions`,
          variant: "destructive"
        });
        return;
      }
      newSelected.add(questionId);
    } else {
      newSelected.delete(questionId);
    }
    setSelectedQuestions(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const availableSlots = maxQuestions - selectedQuestions.size;
      const questionsToAdd = filteredQuestions.slice(0, availableSlots);
      const newSelected = new Set(selectedQuestions);
      questionsToAdd.forEach((q) => newSelected.add(q.id));
      setSelectedQuestions(newSelected);
    } else {
      const newSelected = new Set(selectedQuestions);
      filteredQuestions.forEach((q) => newSelected.delete(q.id));
      setSelectedQuestions(newSelected);
    }
  };

  const handleRandomSelect = () => {
    const availableQuestions = filteredQuestions.filter((q) => !selectedQuestions.has(q.id));
    const availableSlots = maxQuestions - selectedQuestions.size;
    const numToSelect = Math.min(availableSlots, availableQuestions.length, 10);

    const shuffled = [...availableQuestions].sort(() => 0.5 - Math.random());
    const newSelected = new Set(selectedQuestions);
    shuffled.slice(0, numToSelect).forEach((q) => newSelected.add(q.id));
    setSelectedQuestions(newSelected);
  };

  const handleConfirmSelection = () => {
    const selectedQuestionsArray = questions.filter((q) => selectedQuestions.has(q.id));
    onQuestionsSelected(selectedQuestionsArray);

    toast({
      title: "Questions Selected",
      description: `${selectedQuestionsArray.length} questions selected for quiz`
    });
  };

  const getQuestionTypeColor = (type: string) => {
    switch (type) {
      case 'multiple_choice':return 'bg-blue-100 text-blue-800';
      case 'true_false':return 'bg-green-100 text-green-800';
      case 'short_answer':return 'bg-yellow-100 text-yellow-800';
      case 'essay':return 'bg-purple-100 text-purple-800';
      default:return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':return 'bg-green-100 text-green-800';
      case 'medium':return 'bg-yellow-100 text-yellow-800';
      case 'hard':return 'bg-red-100 text-red-800';
      default:return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card data-id="mnlic1utb" data-path="src/components/ai/QuestionSelector.tsx">
      <CardHeader data-id="mwhqezclc" data-path="src/components/ai/QuestionSelector.tsx">
        <CardTitle className="flex items-center gap-2" data-id="taxslkmpx" data-path="src/components/ai/QuestionSelector.tsx">
          <Target className="h-5 w-5" data-id="snt564jv6" data-path="src/components/ai/QuestionSelector.tsx" />
          Select Questions from Pool
        </CardTitle>
        <CardDescription data-id="g2peskhth" data-path="src/components/ai/QuestionSelector.tsx">
          Choose questions from your existing pool to create the quiz ({selectedQuestions.size}/{maxQuestions} selected)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6" data-id="a2fw6tlcr" data-path="src/components/ai/QuestionSelector.tsx">
        {/* Filters */}
        <div className="space-y-4" data-id="bsprhygtu" data-path="src/components/ai/QuestionSelector.tsx">
          <div className="flex flex-col md:flex-row gap-4" data-id="d43j4wqza" data-path="src/components/ai/QuestionSelector.tsx">
            <div className="flex-1 space-y-2" data-id="kwcr9ant1" data-path="src/components/ai/QuestionSelector.tsx">
              <Label htmlFor="search" data-id="n1ahvqygl" data-path="src/components/ai/QuestionSelector.tsx">Search Questions</Label>
              <div className="relative" data-id="e0jtkyp7i" data-path="src/components/ai/QuestionSelector.tsx">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" data-id="mpr2t39au" data-path="src/components/ai/QuestionSelector.tsx" />
                <Input
                  id="search"
                  placeholder="Search by question text, subject, or tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10" data-id="129u3kxzw" data-path="src/components/ai/QuestionSelector.tsx" />

              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-id="mwb6bkz4p" data-path="src/components/ai/QuestionSelector.tsx">
            <div className="space-y-2" data-id="jmk2fjdy0" data-path="src/components/ai/QuestionSelector.tsx">
              <Label data-id="84vw4ruyj" data-path="src/components/ai/QuestionSelector.tsx">Category</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory} data-id="5z3qqq9t6" data-path="src/components/ai/QuestionSelector.tsx">
                <SelectTrigger data-id="jkmgorq0r" data-path="src/components/ai/QuestionSelector.tsx">
                  <SelectValue placeholder="All" data-id="sns99tbnk" data-path="src/components/ai/QuestionSelector.tsx" />
                </SelectTrigger>
                <SelectContent data-id="p1nrddxue" data-path="src/components/ai/QuestionSelector.tsx">
                  <SelectItem value="all" data-id="lvoxywvqu" data-path="src/components/ai/QuestionSelector.tsx">All Categories</SelectItem>
                  {categories.map((category) =>
                  <SelectItem key={category} value={category} data-id="tfisxeyqr" data-path="src/components/ai/QuestionSelector.tsx">
                      {category}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2" data-id="npdozjcq0" data-path="src/components/ai/QuestionSelector.tsx">
              <Label data-id="ts7c7bzu5" data-path="src/components/ai/QuestionSelector.tsx">Difficulty</Label>
              <Select value={filterDifficulty} onValueChange={setFilterDifficulty} data-id="susqs7o79" data-path="src/components/ai/QuestionSelector.tsx">
                <SelectTrigger data-id="5twvoguyo" data-path="src/components/ai/QuestionSelector.tsx">
                  <SelectValue placeholder="All" data-id="k5qapd2wh" data-path="src/components/ai/QuestionSelector.tsx" />
                </SelectTrigger>
                <SelectContent data-id="4mmoh14cn" data-path="src/components/ai/QuestionSelector.tsx">
                  <SelectItem value="all" data-id="gwszod0pu" data-path="src/components/ai/QuestionSelector.tsx">All Levels</SelectItem>
                  <SelectItem value="easy" data-id="o2sbeaclt" data-path="src/components/ai/QuestionSelector.tsx">Easy</SelectItem>
                  <SelectItem value="medium" data-id="ac4m2kfx8" data-path="src/components/ai/QuestionSelector.tsx">Medium</SelectItem>
                  <SelectItem value="hard" data-id="hq12y5zgp" data-path="src/components/ai/QuestionSelector.tsx">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2" data-id="jx37j9chy" data-path="src/components/ai/QuestionSelector.tsx">
              <Label data-id="tacg71351" data-path="src/components/ai/QuestionSelector.tsx">Type</Label>
              <Select value={filterType} onValueChange={setFilterType} data-id="0ufg5fksj" data-path="src/components/ai/QuestionSelector.tsx">
                <SelectTrigger data-id="ydugf6oe2" data-path="src/components/ai/QuestionSelector.tsx">
                  <SelectValue placeholder="All" data-id="t6ffou88n" data-path="src/components/ai/QuestionSelector.tsx" />
                </SelectTrigger>
                <SelectContent data-id="1kj0o0mwv" data-path="src/components/ai/QuestionSelector.tsx">
                  <SelectItem value="all" data-id="siks6se97" data-path="src/components/ai/QuestionSelector.tsx">All Types</SelectItem>
                  <SelectItem value="multiple_choice" data-id="l4zq169jm" data-path="src/components/ai/QuestionSelector.tsx">Multiple Choice</SelectItem>
                  <SelectItem value="true_false" data-id="8u1dd1912" data-path="src/components/ai/QuestionSelector.tsx">True/False</SelectItem>
                  <SelectItem value="short_answer" data-id="a93kapymv" data-path="src/components/ai/QuestionSelector.tsx">Short Answer</SelectItem>
                  <SelectItem value="essay" data-id="4b4gmpiwx" data-path="src/components/ai/QuestionSelector.tsx">Essay</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2" data-id="h77gy46xu" data-path="src/components/ai/QuestionSelector.tsx">
              <Label data-id="lu7gf5ocr" data-path="src/components/ai/QuestionSelector.tsx">Tag Filter</Label>
              <Input
                placeholder="Filter by tag"
                value={filterTag}
                onChange={(e) => setFilterTag(e.target.value)} data-id="d2uh732h6" data-path="src/components/ai/QuestionSelector.tsx" />

            </div>

            <div className="space-y-2" data-id="wuu6fbrm5" data-path="src/components/ai/QuestionSelector.tsx">
              <Label data-id="axtn22p50" data-path="src/components/ai/QuestionSelector.tsx">&nbsp;</Label>
              <Button variant="outline" onClick={handleRandomSelect} className="w-full" data-id="wn8sjpjud" data-path="src/components/ai/QuestionSelector.tsx">
                <Shuffle className="h-4 w-4 mr-2" data-id="g9z1au9sw" data-path="src/components/ai/QuestionSelector.tsx" />
                Random
              </Button>
            </div>
          </div>
        </div>

        <Separator data-id="6g7ws8tmq" data-path="src/components/ai/QuestionSelector.tsx" />

        {/* Selection Controls */}
        <div className="flex justify-between items-center" data-id="rwv55pn43" data-path="src/components/ai/QuestionSelector.tsx">
          <div className="flex items-center space-x-2" data-id="pnd16eczp" data-path="src/components/ai/QuestionSelector.tsx">
            <Checkbox
              id="select-all"
              checked={filteredQuestions.length > 0 && filteredQuestions.every((q) => selectedQuestions.has(q.id))}
              onCheckedChange={handleSelectAll} data-id="pux6plmi2" data-path="src/components/ai/QuestionSelector.tsx" />

            <Label htmlFor="select-all" data-id="4aos4x31j" data-path="src/components/ai/QuestionSelector.tsx">
              Select All Visible ({selectedQuestions.size} selected)
            </Label>
          </div>
          <Button
            onClick={handleConfirmSelection}
            disabled={selectedQuestions.size === 0} data-id="l3kcvt3qt" data-path="src/components/ai/QuestionSelector.tsx">

            <BookOpen className="h-4 w-4 mr-2" data-id="uc1e4osjz" data-path="src/components/ai/QuestionSelector.tsx" />
            Use Selected Questions
          </Button>
        </div>

        {/* Questions List */}
        <div className="space-y-3 max-h-96 overflow-y-auto" data-id="vpdgpcwsm" data-path="src/components/ai/QuestionSelector.tsx">
          {loading ?
          <p className="text-center text-gray-500 py-8" data-id="e6ug6jwvy" data-path="src/components/ai/QuestionSelector.tsx">Loading questions...</p> :
          filteredQuestions.length === 0 ?
          <p className="text-center text-gray-500 py-8" data-id="nrhwxv2lk" data-path="src/components/ai/QuestionSelector.tsx">
              No questions found. Try adjusting your filters.
            </p> :

          filteredQuestions.map((question) =>
          <Card
            key={question.id}
            className={selectedQuestions.has(question.id) ? 'ring-2 ring-blue-500' : ''} data-id="9xbbekjol" data-path="src/components/ai/QuestionSelector.tsx">

                <CardContent className="pt-4" data-id="xzsthlk3t" data-path="src/components/ai/QuestionSelector.tsx">
                  <div className="flex items-start space-x-3" data-id="45rq21cvy" data-path="src/components/ai/QuestionSelector.tsx">
                    <Checkbox
                  checked={selectedQuestions.has(question.id)}
                  onCheckedChange={(checked) => handleSelectQuestion(question.id, checked as boolean)} data-id="d75joo26z" data-path="src/components/ai/QuestionSelector.tsx" />

                    <div className="flex-1" data-id="stayrmnq7" data-path="src/components/ai/QuestionSelector.tsx">
                      <div className="flex items-center gap-2 mb-2" data-id="i5i7nsde3" data-path="src/components/ai/QuestionSelector.tsx">
                        <Badge className={getQuestionTypeColor(question.question_type)} data-id="mwklvyhr0" data-path="src/components/ai/QuestionSelector.tsx">
                          {question.question_type.replace('_', ' ')}
                        </Badge>
                        <Badge className={getDifficultyColor(question.difficulty)} data-id="qj7zedftm" data-path="src/components/ai/QuestionSelector.tsx">
                          {question.difficulty}
                        </Badge>
                        <Badge variant="outline" data-id="k34eirq0t" data-path="src/components/ai/QuestionSelector.tsx">{question.subject}</Badge>
                      </div>
                      <p className="font-medium mb-1" data-id="8t9xs4rg9" data-path="src/components/ai/QuestionSelector.tsx">{question.question_text}</p>
                      <p className="text-sm text-gray-600 mb-2" data-id="69jlt2lk8" data-path="src/components/ai/QuestionSelector.tsx">
                        <strong data-id="k6let2b5y" data-path="src/components/ai/QuestionSelector.tsx">Answer:</strong> {question.answer_text}
                      </p>
                      {question.tags.length > 0 &&
                  <div className="flex gap-1 flex-wrap" data-id="iy4wq6woe" data-path="src/components/ai/QuestionSelector.tsx">
                          {question.tags.map((tag, index) =>
                    <Badge key={index} variant="secondary" className="text-xs" data-id="f32sibfuw" data-path="src/components/ai/QuestionSelector.tsx">
                              {tag}
                            </Badge>
                    )}
                        </div>
                  }
                    </div>
                  </div>
                </CardContent>
              </Card>
          )
          }
        </div>

        {/* Pagination */}
        {totalPages > 1 &&
        <div className="flex justify-center gap-2" data-id="6h9sqersn" data-path="src/components/ai/QuestionSelector.tsx">
            <Button
            variant="outline"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1} data-id="ogrrfh9gd" data-path="src/components/ai/QuestionSelector.tsx">

              Previous
            </Button>
            <span className="flex items-center px-4" data-id="0o95yj54e" data-path="src/components/ai/QuestionSelector.tsx">
              Page {currentPage} of {totalPages}
            </span>
            <Button
            variant="outline"
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages} data-id="0f9ffwi18" data-path="src/components/ai/QuestionSelector.tsx">

              Next
            </Button>
          </div>
        }
      </CardContent>
    </Card>);

};

export default QuestionSelector;